import './styles/index.css';
import { registerSW } from 'virtual:pwa-register';
import { ReconnectOverlay } from './components/Connection/ReconnectOverlay.ts';
import { ToastContainer } from './components/Toast/ToastContainer.ts';
import { initInstallPrompt } from './lib/install-prompt.ts';
import { log } from './lib/logger.ts';
import { initNativeBridge } from './lib/native-bridge.ts';
import {
  initOrientationLock,
  releaseOrientationLock,
  syncOrientationToPage,
} from './lib/orientation-lock.ts';
import { isNative } from './lib/runtime.ts';
import { initSentry } from './lib/sentry.ts';
import { initSwipeNavGuard } from './lib/swipe-nav-guard.ts';
import { applyTheme, initThemeSystemWatcher } from './lib/theme.ts';
import { ErrorPage } from './pages/ErrorPage.ts';
import { GamePage } from './pages/GamePage.ts';
import { JoinPage } from './pages/JoinPage.ts';
import { MainPage } from './pages/MainPage.ts';
import { ResultPage } from './pages/ResultPage.ts';
import { WaitingRoomPage } from './pages/WaitingRoomPage.ts';
import { type Route, router } from './router.ts';
import { BotMatchService } from './services/bot-match.ts';
import { MultiplayerService } from './services/multiplayer.ts';
import { MultiplayerOrchestrator } from './services/multiplayer-orchestrator.ts';
import { getSocket } from './socket/client.ts';
import { roomStore, sessionStore } from './stores/index.ts';

initSentry();
initSwipeNavGuard();
applyTheme();
initThemeSystemWatcher();
if (isNative()) {
  void navigator.serviceWorker?.getRegistrations().then((rs) => {
    rs.forEach((r) => {
      void r.unregister();
    });
  });
} else {
  initOrientationLock();
  initInstallPrompt();
  if (import.meta.env.PROD) {
    registerSW({ immediate: true });
  }
}

const rootEl = document.getElementById('app');
if (!rootEl) {
  throw new Error('#app element not found');
}
const root: HTMLElement = rootEl;

log.info('app.boot', { mode: 'multi-player-shell' });

const toastContainer = new ToastContainer(document.body);
toastContainer.mount();

const reconnectOverlay = new ReconnectOverlay(document.body);
reconnectOverlay.mount();

const socket = getSocket();
const multiplayer = new MultiplayerService(socket);

initNativeBridge({
  onResume: () => {
    if (!socket.connected) socket.connect();
  },
});

type ActivePage = {
  route: Route;
  unmount: () => void;
};

let active: ActivePage | null = null;
let currentSingleService: BotMatchService | null = null;

function ensureSingleService(): BotMatchService {
  if (!currentSingleService) {
    const nick = sessionStore.get().nickname.trim() || '나';
    currentSingleService = new BotMatchService(nick);
  }
  return currentSingleService;
}

function clearSingleService(): void {
  currentSingleService?.dispose();
  currentSingleService = null;
}

const ROTATABLE_ROUTES = new Set<Route['name']>(['play', 'single']);

function renderRoute(route: Route): void {
  if (active) active.unmount();
  document.body.dataset.page = route.name;
  syncOrientationToPage();
  if (!ROTATABLE_ROUTES.has(route.name)) {
    releaseOrientationLock();
  }

  switch (route.name) {
    case 'main': {
      clearSingleService();
      const p = new MainPage(root, multiplayer);
      p.mount();
      active = { route, unmount: () => p.unmount() };
      break;
    }
    case 'join': {
      const p = new JoinPage(root, multiplayer, route.prefilledCode);
      p.mount();
      active = { route, unmount: () => p.unmount() };
      break;
    }
    case 'room': {
      const nick = sessionStore.get().nickname.trim();
      if (!nick) {
        router.navigate({ name: 'join', prefilledCode: route.code }, true);
        return;
      }
      const p = new WaitingRoomPage(root, multiplayer, route.code);
      p.mount();
      active = { route, unmount: () => p.unmount() };
      break;
    }
    case 'play': {
      const room = roomStore.get();
      if (!room) {
        router.navigate({ name: 'main' }, true);
        return;
      }
      const lastState = multiplayer.getLastGameState();
      if (lastState && lastState.status === 'FINISHED') {
        router.navigate({ name: 'result', code: room.roomCode }, true);
        return;
      }
      const orchestrator = new MultiplayerOrchestrator(multiplayer, room.myPlayerId);
      const p = new GamePage(root, orchestrator, {
        onFinished: () => router.navigate({ name: 'result', code: room.roomCode }),
        isMultiplayer: true,
        onLeave: () => {
          multiplayer.leaveRoom();
          router.navigate({ name: 'main' });
        },
      });
      p.mount();
      active = {
        route,
        unmount: () => {
          p.unmount();
          orchestrator.dispose();
        },
      };
      break;
    }
    case 'single': {
      const service = ensureSingleService();
      if (import.meta.env.DEV && sessionStorage.getItem('completto:cheat-finish') === '1') {
        sessionStorage.removeItem('completto:cheat-finish');
        service.forceFinish();
        router.navigate({ name: 'single-result' }, true);
        return;
      }
      if (service.getState().status === 'FINISHED') {
        router.navigate({ name: 'single-result' }, true);
        return;
      }
      const p = new GamePage(root, service, {
        onFinished: () => router.navigate({ name: 'single-result' }),
        isMultiplayer: false,
        onLeave: () => {
          clearSingleService();
          router.navigate({ name: 'main' });
        },
      });
      p.mount();
      active = { route, unmount: () => p.unmount() };
      break;
    }
    case 'result': {
      const room = roomStore.get();
      const state = multiplayer.getLastGameState();
      if (!room || !state || state.status !== 'FINISHED') {
        router.navigate({ name: 'main' }, true);
        return;
      }
      const lastRoom = multiplayer.getLastRoomState();
      const myPlayer = lastRoom?.players.find((p) => p.id === room.myPlayerId);
      const amHost = myPlayer?.isHost ?? false;
      const p = new ResultPage(root, state, room.myPlayerId, {
        amHost,
        onGoMain: () => {
          multiplayer.leaveRoom();
          router.navigate({ name: 'main' });
        },
        onRestart: () => multiplayer.restartGame(),
        autoNavigateOnRestart: {
          subscribeGameState: (fn) => multiplayer.onGameState(fn),
          onGameRestarted: () => router.navigate({ name: 'play', code: room.roomCode }),
        },
        subscribeRoomState: (fn) => multiplayer.onRoomState(fn),
        subscribeError: (fn) => multiplayer.onError(fn),
      });
      p.mount();
      active = { route, unmount: () => p.unmount() };
      break;
    }
    case 'single-result': {
      const service = currentSingleService;
      const state = service?.getState();
      if (!service || !state || state.status !== 'FINISHED') {
        router.navigate({ name: 'main' }, true);
        return;
      }
      const p = new ResultPage(root, state, service.myPlayerId, {
        amHost: true,
        restartLabel: '다시 도전',
        onGoMain: () => {
          clearSingleService();
          router.navigate({ name: 'main' });
        },
        onRestart: () => {
          service.reset?.();
          router.navigate({ name: 'single' });
        },
      });
      p.mount();
      active = { route, unmount: () => p.unmount() };
      break;
    }
    case 'error': {
      const p = new ErrorPage(root, route.reason);
      p.mount();
      active = { route, unmount: () => p.unmount() };
      break;
    }
  }
}

router.subscribe(renderRoute);
renderRoute(router.get());
